import React, { useState } from 'react';
import { Menu, X, ChevronDown } from 'lucide-react';

const Header = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [loginDropdownOpen, setLoginDropdownOpen] = useState(false);

  const navLinks = [
    { name: 'The Journal', href: 'https://blog.entrepreneurladyhustler.com/en/blog' },
    { name: 'Membership', href: 'https://join.entrepreneurladyhustler.com' },
    { name: 'The Société', href: 'https://societe.entrepreneurladyhustler.com' },
    { name: 'Contact', href: 'mailto:hello@entrepreneurladyhustler.com' },
    { name: 'About Us', href: 'https://entrepreneurladyhustler.com/en/leadership/' },
  ];

  const loginOptions = [
    { name: 'Association Login', href: 'https://app.entrepreneurladyhustler.com/login' },
    { name: 'The Société Login', href: 'https://theladyhustlersocietyclub.app.clientclub.net/login' },
  ];

  return (
    <header 
      className="w-full bg-white border-b border-[#DBC8CE]"
      style={{ position: 'sticky', top: 0, zIndex: 1000 }}
    >
      {/* Skip Navigation Link for Accessibility */}
      <a 
        href="#main-content" 
        className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 focus:z-50 focus:bg-[#9D6E6D] focus:text-white focus:px-4 focus:py-2 focus:rounded"
      >
        Skip to main content
      </a>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-20">
          {/* Logo */}
          <a 
            href="https://entrepreneurladyhustler.com/en/"
            className="flex items-center"
            data-testid="header-logo"
          >
            <span className="font-cormorant text-2xl font-semibold text-[#2C2C2C]">
              LHSC
            </span>
            <span className="hidden sm:block ml-2 font-josefin text-xs uppercase tracking-widest text-[#7E8184]">
              The Lady Hustler Society Club
            </span>
          </a>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center space-x-8" data-testid="desktop-nav">
            {navLinks.map((link) => (
              <a
                key={link.name}
                href={link.href}
                className="font-josefin text-sm uppercase tracking-wider text-[#2C2C2C] hover:text-[#9D6E6D] transition-colors duration-200"
                data-testid={`nav-link-${link.name.toLowerCase().replace(/\s+/g, '-')}`}
              >
                {link.name}
              </a>
            ))}
            
            {/* Member Login Dropdown */}
            <div className="relative">
              <button
                onClick={() => setLoginDropdownOpen(!loginDropdownOpen)}
                onBlur={() => setTimeout(() => setLoginDropdownOpen(false), 150)}
                className="font-josefin text-sm uppercase tracking-wider text-[#2C2C2C] hover:text-[#9D6E6D] transition-colors duration-200 flex items-center gap-1"
                data-testid="member-login-dropdown"
              >
                Member Login
                <ChevronDown size={14} className={`transition-transform duration-200 ${loginDropdownOpen ? 'rotate-180' : ''}`} />
              </button>
              
              {loginDropdownOpen && (
                <div className="absolute top-full right-0 mt-2 w-56 bg-white border border-[#DBC8CE] rounded-lg shadow-lg overflow-hidden z-50">
                  {loginOptions.map((option) => (
                    <a
                      key={option.name}
                      href={option.href}
                      className="block px-4 py-3 font-josefin text-sm text-[#2C2C2C] hover:bg-[#F2E1E1] hover:text-[#9D6E6D] transition-colors duration-200"
                      data-testid={`login-option-${option.name.toLowerCase().replace(/\s+/g, '-')}`}
                    >
                      {option.name}
                    </a>
                  ))}
                </div>
              )}
            </div>
          </nav>

          {/* CTA Button - Desktop */}
          <div className="hidden lg:block">
            <a
              href="https://join.entrepreneurladyhustler.com"
              className="font-josefin text-sm uppercase tracking-wider bg-[#9D6E6D] text-white px-6 py-3 rounded hover:bg-[#7E8184] transition-colors duration-200"
              data-testid="header-join-btn"
            >
              Join LHSC
            </a>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="lg:hidden p-2 text-[#2C2C2C]"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label={mobileMenuOpen ? 'Close menu' : 'Open menu'}
            data-testid="mobile-menu-btn"
          >
            {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile Navigation */}
        {mobileMenuOpen && (
          <nav 
            className="lg:hidden py-4 border-t border-[#DBC8CE]"
            data-testid="mobile-nav"
          >
            {navLinks.map((link) => (
              <a
                key={link.name}
                href={link.href}
                className="block py-3 font-josefin text-sm uppercase tracking-wider text-[#2C2C2C]"
                onClick={() => setMobileMenuOpen(false)}
                data-testid={`mobile-nav-link-${link.name.toLowerCase().replace(/\s+/g, '-')}`}
              >
                {link.name}
              </a>
            ))}
            
            {/* Mobile Login Options */}
            <div className="py-3 border-t border-[#DBC8CE] mt-2">
              <span className="block font-josefin text-xs uppercase tracking-wider text-[#7E8184] mb-2">
                Member Login
              </span>
              {loginOptions.map((option) => (
                <a
                  key={option.name}
                  href={option.href}
                  className="block py-2 pl-4 font-josefin text-sm text-[#2C2C2C]"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  {option.name}
                </a>
              ))}
            </div>
            
            <a
              href="https://join.entrepreneurladyhustler.com"
              className="block mt-4 font-josefin text-sm uppercase tracking-wider bg-[#9D6E6D] text-white px-6 py-3 rounded text-center"
              data-testid="mobile-join-btn"
            >
              Join LHSC
            </a>
          </nav>
        )}
      </div>
    </header>
  );
};

export default Header;
